/** Generieke vragenlijst-wizard — v2 (2026-06-01).
 *
 * v2-wijzigingen:
 *  - Vraagteksten + sectie-intro's uit gevalideerde instrumenten (PROMIS / WAS / WRFQ-5 / DSPS-6)
 *  - Schaal-labels per sectie verschillend (was 1× generiek)
 *  - Sectie 3+5+6: schaal-uitleg één keer bovenaan ipv onder elke vraag
 *  - Privacy-tussenstap: korte tekst + link naar /privacy (was lange tekst inline)
 *  - Welkomtekst warmer en klant-centered
 *
 * Baseline-flow  (skipAvg=false): AVG-pagina → 6-step wizard
 * Followup-flow  (skipAvg=true) : direct in 6-step wizard
 *
 * 6 zichtbare stappen:
 *  1. Intake (7)             2. Pijn (1)               3. Bewegen (4)
 *  4. Werkvermogen (1)       5. Werkfunctioneren (5)   6. Werkbeleving (6)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckboxGroupField, FieldRow, RadioGroupField, ScaleField, TextField } from "./FormFields";
import { ErrorBanner, ProgressBar } from "./PageShell";
import { GeboortedatumInfo } from "./GeboortedatumInfo";
import {
  VRAGEN_LABELS, EMPTY_ANTWOORDEN, SECTIE_INTRO,
  BEWEGEN_SCHAAL, WERK_FREQUENTIE_SCHAAL, WERKBELEVING_SCHAAL,
  PIJN_LOCATIE_OPTIES, PIJN_FREQUENTIE_OPTIES, KLACHTEN_ELDERS_OPTIES,
} from "../lib/antwoordenSchema";
import { validateStep, toBackendPayload, STEP_KEY_BY_INDEX, valideerBereik } from "../lib/validators";
import {
  VragenlijstRenderer,
  zichtbareSecties,
  verplichteVeldenVanSectie,
  zichtbareVeldenVanSectie,
} from "./VragenlijstRenderer";

/** True als het schema de data-gestuurde secties-doelvorm heeft (v4). */
function heeftSecties(schema) {
  return !!(schema && Array.isArray(schema.secties) && schema.secties.length > 0);
}

/** Leeg-check die 0 als ingevulde waarde behandelt (schaal-velden). */
function isLeegAntwoord(v) {
  if (v === "" || v === null || v === undefined) return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

const TOTAL_STEPS = 7;
const FINAL_STEP = 7;

export function VragenlijstWizard({
  initialAntwoorden = EMPTY_ANTWOORDEN,
  onChange,
  onSubmit,
  skipAvg = false,
  submitting = false,
  apiError = null,
  intakeDefaults = null,
  progressieToken = null,
  geboortedatumOntbreekt = false,
  onDefaultsUpdate,
  previewMode = false,
  schema = null,
  isVervolg = false,
}) {
  // Expand/contract-fallback: alleen de data-gestuurde weg gebruiken als het
  // schema de nieuwe secties-doelvorm heeft. Prod-v3 (`vragen_json` zonder
  // `secties`) valt terug op de hardcoded 7-staps-weg hieronder — zo blijft de
  // live flow werken tijdens de ombouw.
  const dataDriven = heeftSecties(schema);
  const secties = dataDriven ? zichtbareSecties(schema, isVervolg) : [];

  const [phase, setPhase] = useState(skipAvg ? "wizard" : "avg");
  const [step, setStep] = useState(1);
  const [antwoorden, setAntwoorden] = useState({ ...EMPTY_ANTWOORDEN, ...initialAntwoorden });
  const [stepError, setStepError] = useState(null);
  const [foutBerichten, setFoutBerichten] = useState({}); // { veld_id: melding } (v4-weg)
  const [avgChecked, setAvgChecked] = useState(false);

  function update(key, value) {
    setAntwoorden((cur) => {
      const next = { ...cur, [key]: value };
      onChange?.(next);
      return next;
    });
  }

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, phase]);

  // Sync leeftijd uit intakeDefaults zodra die binnen zijn (race-safe)
  useEffect(() => {
    if (intakeDefaults?.leeftijd && !antwoorden.leeftijd) {
      setAntwoorden((cur) => {
        const next = { ...cur, leeftijd: intakeDefaults.leeftijd };
        onChange?.(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeDefaults?.leeftijd]);

  // Effectieve stap-grenzen: data-gestuurd = aantal zichtbare secties.
  const totalSteps = dataDriven ? secties.length : TOTAL_STEPS;
  const finalStep = dataDriven ? secties.length : FINAL_STEP;

  function nextStep() {
    setStepError(null);
    setFoutBerichten({});

    if (dataDriven) {
      // v4-weg: leeg-check + bereik-/typecontrole tegen het schema, met
      // veld-markering en een veld-specifieke melding. Geen dood spoor meer.
      const sectie = secties[step - 1];
      const labelVoor = (f) => schema?.velden?.[f]?.vraag || VRAGEN_LABELS[f] || f;
      const missing = sectie
        ? verplichteVeldenVanSectie(sectie, schema, antwoorden).filter((f) => isLeegAntwoord(antwoorden?.[f]))
        : [];
      const zichtbaar = sectie ? zichtbareVeldenVanSectie(sectie, schema, antwoorden) : [];
      const bereikFouten = valideerBereik(zichtbaar, schema, antwoorden);

      if (missing.length || bereikFouten.length) {
        const berichtenPerVeld = {};
        missing.forEach((f) => { berichtenPerVeld[f] = "Deze vraag is nog niet ingevuld."; });
        // Bereik-fouten winnen van de leeg-melding als beide op één veld slaan.
        bereikFouten.forEach(({ veldId, message }) => { berichtenPerVeld[veldId] = message; });
        setFoutBerichten(berichtenPerVeld);

        const regels = [];
        if (missing.length) {
          const namen = missing.map(labelVoor);
          regels.push(
            namen.length === 1
              ? `Deze vraag is nog niet ingevuld: ${namen[0]}`
              : `Deze vragen zijn nog niet ingevuld: ${namen.join(" · ")}`,
          );
        }
        bereikFouten.forEach(({ message }) => regels.push(message));
        setStepError({ message: regels.join(" ") });

        const eersteId = missing[0] || bereikFouten[0]?.veldId;
        scrollNaarVeld(eersteId);
        return;
      }
    } else {
      const stepKey = STEP_KEY_BY_INDEX[step];
      const missing = validateStep(stepKey, antwoorden);
      if (missing.length) {
        const namen = missing.map((f) => VRAGEN_LABELS[f] || f);
        setStepError({
          message:
            namen.length === 1
              ? `Deze vraag is nog niet ingevuld: ${namen[0]}`
              : `Deze vragen zijn nog niet ingevuld: ${namen.join(" · ")}`,
        });
        return;
      }
    }

    if (step < finalStep) setStep(step + 1);
    else handleSubmit();
  }

  /** Scroll het eerste foute veld in beeld zodat de gebruiker het meteen ziet. */
  function scrollNaarVeld(veldId) {
    if (!veldId || typeof document === "undefined") return;
    const domId = `veld-rij-${String(veldId).replace(/_/g, "-")}`;
    // Wacht op de re-render zodat de gemarkeerde rij bestaat.
    setTimeout(() => {
      const el = document.getElementById(domId);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  }

  function prevStep() {
    setStepError(null);
    setFoutBerichten({});
    if (step > 1) setStep(step - 1);
  }

  function acceptAvg() {
    setStepError(null);
    if (!avgChecked) {
      setStepError({ message: "U moet akkoord gaan met de privacy-voorwaarden om door te gaan." });
      return;
    }
    setPhase("wizard");
    setStep(1);
  }

  async function handleSubmit() {
    setStepError(null);
    if (previewMode) {
      toast.info("Preview-modus: antwoorden zijn niet opgeslagen");
      setAntwoorden({ ...EMPTY_ANTWOORDEN });
      setStep(1);
      setPhase(skipAvg ? "wizard" : "avg");
      setAvgChecked(false);
      onChange?.({ ...EMPTY_ANTWOORDEN });
      return;
    }
    const payload = toBackendPayload(antwoorden, dataDriven ? schema : null);
    await onSubmit?.(payload);
  }

  // ============== PHASE: AVG ==============
  if (phase === "avg") {
    return (
      <div data-testid="wizard">
        <ErrorBanner error={apiError || stepError} />
        <section data-testid="step-avg" aria-labelledby="avg-heading">
          <h2 id="avg-heading" className="fulkra-avgkop">
            Privacy &amp; toestemming
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            Uw antwoorden worden uitsluitend geanonimiseerd aan uw werkgever gerapporteerd — alleen geaggregeerde scores per groep van minimaal 5 werknemers. Uw individuele antwoorden zijn alleen zichtbaar voor de podotherapeut.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Lees de volledige{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
              privacy-voorwaarden
            </Link>
            {" "}voor wat we opslaan, hoe lang, en uw rechten.
          </p>
          <label className="mt-5 flex items-start gap-3" data-testid="avg-checkbox-label">
            <input
              type="checkbox"
              data-testid="avg-checkbox"
              checked={avgChecked}
              onChange={(e) => setAvgChecked(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-slate-400 text-blue-600 focus:ring-blue-500"
              aria-describedby="avg-heading"
            />
            <span className="text-sm text-slate-800">
              Ik ga akkoord met de privacy-voorwaarden.
            </span>
          </label>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={acceptAvg}
              disabled={submitting}
              data-testid="btn-avg-accept"
              className="fulkra-wizardknop fulkra-wizardknop--primair"
            >
              Akkoord en doorgaan
            </button>
          </div>
        </section>
      </div>
    );
  }

  // ============== PHASE: WIZARD — DATA-GESTUURD (v4, secties uit schema) ==============
  if (dataDriven) {
    const huidigeSectie = secties[Math.min(step, secties.length) - 1];
    return (
      <div data-testid="wizard">
        <ProgressBar current={step} total={totalSteps} />
        <ErrorBanner error={apiError || stepError} />

        {huidigeSectie && (
          <VragenlijstRenderer
            schema={schema}
            antwoorden={antwoorden}
            onUpdate={update}
            sectieKeys={[huidigeSectie.key]}
            isVervolg={isVervolg}
            foutBerichten={foutBerichten}
          />
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={prevStep}
            disabled={step === 1 || submitting}
            data-testid="btn-vorige"
            className="fulkra-wizardknop fulkra-wizardknop--secundair"
          >
            Vorige
          </button>
          <button
            type="button"
            onClick={nextStep}
            disabled={submitting}
            data-testid={step === finalStep ? "btn-submit" : "btn-volgende"}
            className="fulkra-wizardknop fulkra-wizardknop--primair"
          >
            {submitting ? "Bezig…" : step === finalStep ? "Versturen" : "Volgende"}
          </button>
        </div>
      </div>
    );
  }

  // ============== PHASE: WIZARD (steps 1-6) ==============
  return (
    <div data-testid="wizard">
      <ProgressBar current={step} total={TOTAL_STEPS} />
      <ErrorBanner error={apiError || stepError} />

      {step === 1 && (
        <section data-testid="step-intake" aria-labelledby="step-intake-heading">
          <h2 id="step-intake-heading" className="fulkra-stapkop">
            Welkom bij de nulmeting van Fulkra Works
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Door middel van een korte vragenlijst kijken we hoe het staan en lopen op dit moment voor u gaat. Daar waar nodig zorgen we dat dat beter wordt — uw antwoorden helpen ons om u zo gericht mogelijk te begeleiden.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Invultijd: ongeveer 5–7 minuten. We beginnen met enkele gegevens over u en uw werk.
          </p>
          <div className="mt-6 space-y-4">
            {/* Ook tonen als `intakeDefaults` leeg is: dan vraagt dit blok om de
                geboortedatum. Stond hier `intakeDefaults && …`, dan zag een deelnemer
                zonder geboortedatum helemaal niets en bleef hij op stap 1 hangen met een
                verplichte leeftijd die nergens in te vullen was.

                Bewust zónder `!skipAvg`: dit is geen privacy-onderdeel. De vervolgmeting
                slaat de AVG-stap over maar heeft dezelfde verplichte leeftijd, dus daar
                liep dezelfde weg dood. */}
            {progressieToken ? (
              <GeboortedatumInfo
                defaults={intakeDefaults}
                ontbreekt={geboortedatumOntbreekt}
                progressieToken={progressieToken}
                onUpdate={(nieuwe) => { onDefaultsUpdate?.(nieuwe); }}
              />
            ) : null}
            <FieldRow label={VRAGEN_LABELS.geslacht} required>
              <RadioGroupField name="geslacht" value={antwoorden.geslacht} onChange={(v) => update("geslacht", v)} testid="field-geslacht"
                options={[{ value: "man", label: "Man" }, { value: "vrouw", label: "Vrouw" }, { value: "anders", label: "Anders" }]} />
            </FieldRow>
            <FieldRow label={VRAGEN_LABELS.lengte_cm} required>
              <TextField name="lengte_cm" type="text" inputMode="numeric" value={antwoorden.lengte_cm} onChange={(v) => update("lengte_cm", v)} testid="field-lengte-cm" min="120" max="220" />
            </FieldRow>
            <FieldRow label={VRAGEN_LABELS.gewicht_kg} required>
              <TextField name="gewicht_kg" type="text" inputMode="numeric" value={antwoorden.gewicht_kg} onChange={(v) => update("gewicht_kg", v)} testid="field-gewicht-kg" min="30" max="250" />
            </FieldRow>
            <FieldRow label={VRAGEN_LABELS.functie_sector} required>
              <TextField name="functie_sector" value={antwoorden.functie_sector} onChange={(v) => update("functie_sector", v)} testid="field-functie-sector" placeholder="bv. verpleegkundige, magazijn, kapper" />
            </FieldRow>
            <FieldRow label={VRAGEN_LABELS.uur_staan_lopen} required>
              <TextField name="uur_staan_lopen" type="text" inputMode="decimal" value={antwoorden.uur_staan_lopen} onChange={(v) => update("uur_staan_lopen", v)} testid="field-uur-staan-lopen" placeholder="bv. 7,5" />
            </FieldRow>
            <FieldRow label={VRAGEN_LABELS.type_werkschoen} required>
              <TextField name="type_werkschoen" value={antwoorden.type_werkschoen} onChange={(v) => update("type_werkschoen", v)} testid="field-type-werkschoen" placeholder="bv. S3 veiligheidsschoen" />
            </FieldRow>
            <FieldRow label={VRAGEN_LABELS.voet_klachten_voorgeschiedenis}>
              <TextField name="voet_klachten_voorgeschiedenis" value={antwoorden.voet_klachten_voorgeschiedenis} onChange={(v) => update("voet_klachten_voorgeschiedenis", v)} testid="field-voet-klachten" placeholder="bv. hielspoor in 2023, fysiotherapie gehad" />
            </FieldRow>
          </div>
        </section>
      )}

      {step === 2 && (
        <section data-testid="step-pijn" aria-labelledby="step-pijn-heading">
          <h2 id="step-pijn-heading" className="fulkra-stapkop">2. Pijn</h2>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
            <p className="text-sm font-medium text-slate-800">
              {VRAGEN_LABELS.pijn_voet} <span className="text-rose-600">*</span>
            </p>
            <ScaleField name="pijn_voet" value={antwoorden.pijn_voet} onChange={(v) => update("pijn_voet", v)}
              min={0} max={10} testid="field-pijn-voet"
              labels={{ min: "0 = geen pijn", max: "10 = ergst denkbare pijn" }} />
          </div>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
            <p className="text-sm font-medium text-slate-800">
              {VRAGEN_LABELS.pijn_locaties} <span className="text-rose-600">*</span>
            </p>
            <CheckboxGroupField
              name="pijn_locaties"
              value={antwoorden.pijn_locaties || []}
              onChange={(v) => update("pijn_locaties", v)}
              options={PIJN_LOCATIE_OPTIES}
              testid="field-pijn-locaties"
              exclusive="geen"
            />
          </div>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
            <p className="text-sm font-medium text-slate-800">
              {VRAGEN_LABELS.pijn_frequentie} <span className="text-rose-600">*</span>
            </p>
            <RadioGroupField
              name="pijn_frequentie"
              value={antwoorden.pijn_frequentie}
              onChange={(v) => update("pijn_frequentie", v)}
              options={PIJN_FREQUENTIE_OPTIES}
              testid="field-pijn-frequentie"
            />
          </div>
        </section>
      )}

      {step === 3 && (
        <SectieMetIntro
          testid="step-bewegen"
          titel="3. Bewegen in het dagelijks leven"
          intro={SECTIE_INTRO.bewegen}
          schaal={BEWEGEN_SCHAAL}
          velden={["bewegen_huishouden", "bewegen_traplopen", "bewegen_wandelen", "bewegen_boodschappen"]}
          antwoorden={antwoorden}
          onUpdate={update}
        />
      )}

      {step === 4 && (
        <section data-testid="step-vermoeidheid-werkvermogen" aria-labelledby="step-vw-heading">
          <h2 id="step-vw-heading" className="fulkra-stapkop">4. Vermoeidheid en werkvermogen</h2>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
            <p className="text-sm font-medium text-slate-800">
              {VRAGEN_LABELS.vermoeidheid_einde_werkdag} <span className="text-rose-600">*</span>
            </p>
            <ScaleField name="vermoeidheid_einde_werkdag" value={antwoorden.vermoeidheid_einde_werkdag} onChange={(v) => update("vermoeidheid_einde_werkdag", v)}
              min={0} max={10} testid="field-vermoeidheid"
              labels={{ min: "0 = helemaal niet vermoeid", max: "10 = totaal uitgeput" }} />
          </div>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
            <p className="text-sm font-medium text-slate-800">
              {VRAGEN_LABELS.werkvermogen} <span className="text-rose-600">*</span>
            </p>
            <ScaleField name="werkvermogen" value={antwoorden.werkvermogen} onChange={(v) => update("werkvermogen", v)}
              min={0} max={10} testid="field-werkvermogen"
              labels={{ min: "0 = ik kan helemaal niet werken", max: "10 = mijn werkvermogen op zijn best" }} />
          </div>
        </section>
      )}

      {step === 5 && (
        <SectieMetIntro
          testid="step-werkfunctioneren"
          titel="5. Werkfunctioneren"
          intro={SECTIE_INTRO.werkfunctioneren}
          schaal={WERK_FREQUENTIE_SCHAAL}
          velden={["werk_start", "werk_fouten", "werk_bewegingen", "werk_concentratie", "werk_multitasken"]}
          antwoorden={antwoorden}
          onUpdate={update}
        />
      )}

      {step === 6 && (
        <SectieMetIntro
          testid="step-werkbeleving"
          titel="6. Werkbeleving"
          intro={SECTIE_INTRO.werkbeleving}
          schaal={WERKBELEVING_SCHAAL}
          velden={["werkbeleving_stress", "werkbeleving_taken", "werkbeleving_plezier", "werkbeleving_zelfvertrouwen", "werkbeleving_concentratie", "werkbeleving_energie"]}
          antwoorden={antwoorden}
          onUpdate={update}
        />
      )}

      {step === 7 && (
        <section data-testid="step-klachten-elders" aria-labelledby="step-klachten-heading">
          <h2 id="step-klachten-heading" className="fulkra-stapkop">7. Klachten elders</h2>
          <p className="fulkra-stapintro">{VRAGEN_LABELS.klachten_elders}</p>

          <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
            <CheckboxGroupField
              name="klachten_elders"
              value={antwoorden.klachten_elders || []}
              onChange={(v) => update("klachten_elders", v)}
              options={KLACHTEN_ELDERS_OPTIES}
              testid="field-klachten-elders"
              exclusive="geen"
            />
          </div>

          {(antwoorden.klachten_elders || []).includes("anders") && (
            <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
              <p className="text-sm font-medium text-slate-800">{VRAGEN_LABELS.klachten_elders_anders}</p>
              <TextField name="klachten_elders_anders" value={antwoorden.klachten_elders_anders} onChange={(v) => update("klachten_elders_anders", v)} testid="field-klachten-elders-anders" placeholder="bv. schouder, pols" />
            </div>
          )}
        </section>
      )}

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={prevStep}
          disabled={step === 1 || submitting}
          data-testid="btn-vorige"
          className="fulkra-wizardknop fulkra-wizardknop--secundair"
        >
          Vorige
        </button>
        <button
          type="button"
          onClick={nextStep}
          disabled={submitting}
          data-testid={step === FINAL_STEP ? "btn-submit" : "btn-volgende"}
          className="fulkra-wizardknop fulkra-wizardknop--primair"
        >
          {submitting ? "Bezig…" : step === FINAL_STEP ? "Versturen" : "Volgende"}
        </button>
      </div>
    </div>
  );
}

function SectieMetIntro({ testid, titel, intro, schaal, velden, antwoorden, onUpdate }) {
  return (
    <section data-testid={testid} aria-labelledby={`${testid}-heading`}>
      <h2 id={`${testid}-heading`} className="fulkra-stapkop">{titel}</h2>
      <p className="fulkra-stapintro">{intro}</p>

      <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <span className="font-medium">Schaal:</span>{" "}
        1 = {schaal.labels[1]} &nbsp;·&nbsp; 2 = {schaal.labels[2]} &nbsp;·&nbsp; 3 = {schaal.labels[3]} &nbsp;·&nbsp; 4 = {schaal.labels[4]} &nbsp;·&nbsp; 5 = {schaal.labels[5]}
      </div>

      <div className="mt-4 space-y-4">
        {velden.map((k) => (
          <div key={k} className="rounded-md border border-slate-200 bg-white p-3">
            <p className="mb-2 text-sm font-medium text-slate-800">
              {VRAGEN_LABELS[k]} <span className="text-rose-600">*</span>
            </p>
            <ScaleField
              name={k}
              value={antwoorden[k]}
              onChange={(v) => onUpdate(k, v)}
              min={1}
              max={5}
              testid={`field-${k.replace(/_/g, "-")}`}
              ariaLabel={VRAGEN_LABELS[k]}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
